import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { IMAGING_MODALITIES, IMAGING_MODALITY_LABELS, type ImagingModality } from '@wodi/domain';
import type { ImagingProcedureView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { imagingProceduresQuery, updateImagingProcedure } from '../api/imaging.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { modalityWords } from './format.ts';
import styles from './imaging.module.css';

// Settings > Imaging (docs/plans/phase2-radiology.md §1.1 item 9): for each imaging test
// in the catalogue, its modality, body part, patient preparation and report template.
// Tests and prices are added in Services and prices. The preparation and template are the
// facility's own words; Wodi ships none (plan R10, R11).

const MODALITY_OPTIONS = IMAGING_MODALITIES.map((m) => ({
  id: m,
  label: IMAGING_MODALITY_LABELS[m],
}));

function EditDialog({ item, onClose }: { item: ImagingProcedureView; onClose: () => void }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [modality, setModality] = useState<ImagingModality | null>(item.modality);
  const [bodyPart, setBodyPart] = useState(item.bodyPart ?? '');
  const [preparation, setPreparation] = useState(item.preparation ?? '');
  const [template, setTemplate] = useState(item.reportTemplate ?? '');
  const [tried, setTried] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      updateImagingProcedure(item.serviceItemId, {
        modality: modality ?? 'other',
        bodyPart,
        preparation,
        reportTemplate: template,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['imaging'] });
      toasts.show({ title: 'Imaging test saved', description: item.name });
      onClose();
    },
  });
  return (
    <Dialog
      title={item.name}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <Select
          label="Modality"
          options={MODALITY_OPTIONS}
          placeholder="Choose the modality"
          value={modality}
          onChange={(v) => {
            setModality(v as ImagingModality);
          }}
          {...(tried && modality === null ? { errorMessage: 'Choose the modality.' } : {})}
        />
        <TextField
          label="Body part (optional)"
          width="md"
          value={bodyPart}
          onChange={setBodyPart}
          maxLength={80}
        />
        <TextArea
          label="Patient preparation (optional)"
          description="Shown when the exam is scheduled. Use the wording your radiology team has approved."
          value={preparation}
          onChange={setPreparation}
          rows={3}
          maxLength={1000}
        />
        <TextArea
          label="Report template (optional)"
          description="Headings the reporter starts from in the findings. Your radiologist's own words."
          value={template}
          onChange={setTemplate}
          rows={5}
          maxLength={4000}
        />
        {save.isError ? (
          <Notice tone="error">
            {errorText(save.error, "The imaging test couldn't be saved.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            setTried(true);
            if (modality !== null) save.mutate();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ImagingSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'imaging.catalogue.manage');
  const items = useQuery({ ...imagingProceduresQuery, enabled: allowed });
  const online = useOnline();
  const [editing, setEditing] = useState<ImagingProcedureView | null>(null);
  if (!allowed)
    return (
      <>
        <PageHeader title="Imaging" />
        <Notice tone="info">
          You don't have permission to set up imaging tests. Ask an administrator if you need it.
        </Notice>
      </>
    );
  return (
    <SettingsPage title="Imaging">
      <Section
        title="Imaging tests"
        description="Add imaging tests and their prices in Services and prices; set how each is done here."
      >
        {items.isPending ? <LoadingBlock label="Loading imaging tests" rows={4} /> : null}
        {items.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void items.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(items.error, "Imaging tests couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {items.isSuccess ? (
          <TableScroll>
            <Table flat dense aria-label="Imaging tests">
              <TableHeader>
                <Column isRowHeader>Test</Column>
                <Column>Modality</Column>
                <Column>Body part</Column>
                <Column>Preparation</Column>
                <Column>Template</Column>
                <Column>Action</Column>
              </TableHeader>
              <TableBody
                items={items.data.items}
                renderEmptyState={() =>
                  'No imaging tests in the catalogue yet. Add them in Services and prices.'
                }
              >
                {(i) => (
                  <Row id={i.serviceItemId}>
                    <Cell>
                      <span className={styles.stack}>
                        <span>{i.name}</span>
                        <span className={`wodi-id ${styles.meta}`}>{i.code}</span>
                      </span>
                    </Cell>
                    <Cell>
                      {i.modality === null ? (
                        <Tag state="attention">Not set</Tag>
                      ) : (
                        modalityWords(i.modality)
                      )}
                    </Cell>
                    <Cell>{i.bodyPart ?? ''}</Cell>
                    <Cell>{i.preparation === null ? 'None' : 'Set'}</Cell>
                    <Cell>{i.reportTemplate === null ? 'None' : 'Set'}</Cell>
                    <Cell>
                      <Button
                        dense
                        isDisabled={!online}
                        aria-label={`Edit ${i.name}`}
                        onPress={() => {
                          setEditing(i);
                        }}
                      >
                        Edit
                      </Button>
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        ) : null}
      </Section>
      {editing === null ? null : (
        <EditDialog
          item={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </SettingsPage>
  );
}
